import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import {
	getCustomersSelector,
	getIsCustomersLoadingSelector,
	getAddressesOptionsSelector,
	getAddressSearchValueSelector,
	getContactSearchValueSelector,
	getContactsOptionsSelector,
	getContactsSelector,
	getFormInitialValuesSelector,
	getFormValueSelector,
	getIsAddressesLoadingSelector,
	getIsContactsLoadingSelector,
	getIsModesLoadingSelector,
	getIsOrderInfoLoadingSelector,
	getIsPartnersLoadingSelector,
	getModesSelector,
	getPartnerSearchValueSelector,
	getPartnersOptionsSelector,
} from '../../selectors';
import {
	getAddressesAction,
	getContactsAction,
	getCustomersAction,
	getModesAction,
	getOrderInfoAction,
	getPartnersAction,
	setAddressSearchValueAction,
	setContactsAction,
	setContactSearchValueAction,
	setOrderInfoAction,
	setPartnerSearchValueAction,
} from '../../actions';
import { OrderFormView } from './components/order-form-view';
import { useInjectSaga } from '../../../../utils/injectSaga';
import { useInjectReducer } from '../../../../utils/injectReducer';
import saga from '../../saga';
import reducer from '../../reducer';
import { FORM_NAME } from '../../constants';

const OrdersFormWrapper = ({
	onGetOrderInfo,
	orderInfo,
	customers,
	isCustomersLoading,
	partners,
	onSetPartnerSearchValue,
	partnerSearchValue,
	addresses,
	onGetAddresses,
	addressSearchValue,
	onSetAddressSearchValue,
	contacts,
	contactSearchValue,
	contactsOptions,
	onSetContactSearchValue,
	onSetContacts,
	onGetContacts,
	modes,
	isPartnersLoading,
	isAddressesLoading,
	isContactsLoading,
	isModesLoading,
	isOrderInfoLoading,
	onGetCustomers,
	onGetPartners,
	onGetModes,
	formValues,
	onFieldChange,
}) => {
	useInjectSaga({ key: 'ordersModalSaga', saga });
	useInjectReducer({ key: 'ordersModalReducer', reducer });

	useEffect(() => {
		onGetCustomers();
		onGetPartners();
		onGetModes();
	}, []);

	useEffect(() => {
		if (orderInfo?.id) {
			onGetOrderInfo(orderInfo.id);
		}
	}, []);

	const handlePartnerSearchChange = ({ searchQuery }, index) => {
		onSetPartnerSearchValue({ ...partnerSearchValue, [index]: searchQuery });
	};

	const handlePartnerChange = (value, index) => {
		const currentPartner = partners[index].find(partner => partner.id === value);

		if (currentPartner) {
			if (partnerSearchValue[index]) {
				onSetPartnerSearchValue({ ...partnerSearchValue, [index]: '' });
			}

			if (contactSearchValue[index]) {
				onSetContactSearchValue({ ...contactSearchValue, [index]: '' });
			}

			if (addressSearchValue[index]) {
				onSetAddressSearchValue({ ...addressSearchValue, [index]: '' });
			}

			onFieldChange(FORM_NAME, `points[${index}].address.id`, '');
			onFieldChange(FORM_NAME, `points[${index}].contact.id`, '');
		}

		onGetAddresses({ id: value, index });
		onSetContacts({ ...contactsOptions, [index]: [] });
	};

	const handleAddressSearchChange = ({ searchQuery }, index) => {
		onSetAddressSearchValue({ ...addressSearchValue, [index]: searchQuery });
	};

	const handleAddressChange = (value, index) => {
		const currentAddress = addresses[index].find(address => address.id === value);

		if (currentAddress) {
			if (contactSearchValue[index]) {
				onSetContactSearchValue({ ...contactSearchValue, [index]: '' });
			}

			if (addressSearchValue[index]) {
				onSetAddressSearchValue({ ...addressSearchValue, [index]: '' });
			}

			onFieldChange(FORM_NAME, `points[${index}].contact.id`, '');
		}

		onGetContacts({ id: value, index });
	};

	const handleContactSearchChange = ({ searchQuery }, index) => {
		onSetContactSearchValue({ ...contactSearchValue, [index]: searchQuery });
	};

	const handleContactChange = (value, index) => {
		const currentContact = contacts[index].find(contact => contact.id === value);
		if (currentContact) {
			if (contactSearchValue[index]) {
				onSetContactSearchValue({ ...contactSearchValue, [index]: '' });
			}
		}
	};

	return (
		<OrderFormView
			customers={customers}
			isCustomersLoading={isCustomersLoading}
			partners={partners}
			addresses={addresses}
			contacts={contacts}
			onPartnerSearchChange={handlePartnerSearchChange}
			onAddressSearchChange={handleAddressSearchChange}
			onContactSearchChange={handleContactSearchChange}
			onPartnerChange={handlePartnerChange}
			partnerSearchValue={partnerSearchValue}
			addressSearchValue={addressSearchValue}
			onAddressChange={handleAddressChange}
			onContactChange={handleContactChange}
			contactSearchValue={contactSearchValue}
			modes={modes}
			isPartnersLoading={isPartnersLoading}
			isAddressesLoading={isAddressesLoading}
			isContactsLoading={isContactsLoading}
			isModesLoading={isModesLoading}
			isOrderInfoLoading={isOrderInfoLoading}
			orderInfo={orderInfo}
			formValues={formValues}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	orderInfo: getFormInitialValuesSelector(),
	customers: getCustomersSelector(),
	isCustomersLoading: getIsCustomersLoadingSelector(),
	partners: getPartnersOptionsSelector(),
	partnerSearchValue: getPartnerSearchValueSelector(),
	addresses: getAddressesOptionsSelector(),
	addressSearchValue: getAddressSearchValueSelector(),
	contacts: getContactsOptionsSelector(),
	contactSearchValue: getContactSearchValueSelector(),
	contactsOptions: getContactsSelector(),
	isPartnersLoading: getIsPartnersLoadingSelector(),
	isAddressesLoading: getIsAddressesLoadingSelector(),
	isContactsLoading: getIsContactsLoadingSelector(),
	isModesLoading: getIsModesLoadingSelector(),
	isOrderInfoLoading: getIsOrderInfoLoadingSelector(),
	modes: getModesSelector(),
	formValues: getFormValueSelector(),
});

const mapDispatchToProps = {
	onGetOrderInfo: getOrderInfoAction,
	onSetOrderInfo: setOrderInfoAction,
	onSetPartnerSearchValue: setPartnerSearchValueAction,
	onGetAddresses: getAddressesAction,
	onFieldChange: change,
	onSetAddressSearchValue: setAddressSearchValueAction,
	onSetContactSearchValue: setContactSearchValueAction,
	onSetContacts: setContactsAction,
	onGetContacts: getContactsAction,
	onGetCustomers: getCustomersAction,
	onGetPartners: getPartnersAction,
	onGetModes: getModesAction,
};

OrdersFormWrapper.propTypes = {
	onFieldChange: PropTypes.func.isRequired,
	customers: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.number.isRequired,
			value: PropTypes.number.isRequired,
			text: PropTypes.string.isRequired,
		}),
	).isRequired,
	isCustomersLoading: PropTypes.bool.isRequired,
	partners: PropTypes.shape({
		0: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.number.isRequired,
				value: PropTypes.number.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
		1: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.number.isRequired,
				value: PropTypes.number.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
	}),
	addresses: PropTypes.shape({
		0: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.number.isRequired,
				value: PropTypes.number.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
		1: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.number.isRequired,
				value: PropTypes.number.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
	}),
	contacts: PropTypes.shape({
		0: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.number.isRequired,
				value: PropTypes.number.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
		1: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.number.isRequired,
				value: PropTypes.number.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
	}),
	onGetOrderInfo: PropTypes.func.isRequired,
	onSetPartnerSearchValue: PropTypes.func.isRequired,
	onGetAddresses: PropTypes.func.isRequired,
	partnerSearchValue: PropTypes.shape({ 0: PropTypes.string, 1: PropTypes.string }).isRequired,
	addressSearchValue: PropTypes.shape({ 0: PropTypes.string, 1: PropTypes.string }).isRequired,
	contactSearchValue: PropTypes.shape({ 0: PropTypes.string, 1: PropTypes.string }).isRequired,
	onSetAddressSearchValue: PropTypes.func.isRequired,
	orderInfo: PropTypes.object,
	contactsOptions: PropTypes.object.isRequired,
	onSetContactSearchValue: PropTypes.func.isRequired,
	onSetContacts: PropTypes.func.isRequired,
	onGetContacts: PropTypes.func.isRequired,
	onGetCustomers: PropTypes.func.isRequired,
	onGetPartners: PropTypes.func.isRequired,
	onGetModes: PropTypes.func.isRequired,
	onSetOrderInfo: PropTypes.func.isRequired,
	modes: PropTypes.array.isRequired,
	isPartnersLoading: PropTypes.bool.isRequired,
	isAddressesLoading: PropTypes.object.isRequired,
	isContactsLoading: PropTypes.object.isRequired,
	isModesLoading: PropTypes.bool.isRequired,
	isOrderInfoLoading: PropTypes.bool.isRequired,
	formValues: PropTypes.object.isRequired,
};

const OrderFormContainer = connect(mapStateToProps, mapDispatchToProps)(OrdersFormWrapper);

export { OrderFormContainer };
